import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Close, KeyboardAltOutlined, MoreVert } from '@mui/icons-material'
import { Box, IconButton, Stack, Tooltip } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useUpdateDocsOrgWithDocChange } from '@/hooks/doc-changes-wrapper-hooks'
import useOverlay, { OverlayState } from '@/hooks/useOverlay'
import { useSetDocumentsWorkflowState } from '@/service-library/hooks/document-workflow-states'
import {
  useGetDocument,
  useUpdateDocument,
} from '@/service-library/hooks/documents'
import { Document } from '@/types/documents'
import { Organization } from '@/types/organizations'
import { WorkflowState } from '@/types/workflows'
import { getShortcuts } from '@/utils/document-overlay'
import { showErrorSnackbar } from '@/utils/snackbars'
import DocumentActionsMenu from '@/components/document-actions-menu/DocumentActionsMenu'
import EditableDocumentName from '@/components/editable-document-name/EditableDocumentName'
import KeyboardShortcutsDialog from '@/components/keyboard-shortcuts-popover/KeyboardShortcutsDialog'
import { useNotifications } from '@/components/notifications/NotificationProvider'
import OrganizationAssignmentSelect from '@/components/organization-select/OrganizationAssignmentSelect'
import { useOrganizationsContext } from '@/components/organizations/OrganizationsProvider'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import DocProjectTagsList from '@/components/tags/DocProjectTagsList'
import DocumentViewControl from '@/components/validation/DocumentViewControl'
import { useDocumentRowValuesContext } from '@/components/validation/providers/DocumentRowValuesProvider'
import { useDocumentsChangeSetsContext } from '@/components/validation/providers/DocumentsChangeSetsProvider'
import { useSelectedFieldContext } from '@/components/validation/providers/SelectedFieldProvider'
import ValidationEventButton from '@/components/validation/ValidationEventButton'
import VisibleCardsControl from '@/components/validation/VisibleCardsControl'
import VisibleFieldsControl from '@/components/validation/VisibleFieldsControl'
import WorkflowStateSelectMenu from '@/components/workflow-state-select-menu/WorkflowStateSelectMenu'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'
import ValidationFlagsButton from '../flags/ValidationFlagsButton'
import DocumentNavigationButtons from './DocumentNavigationButtons'
import DrawerToggleButtons from './DrawerToggleButtons'

type ValidationOverlayHeaderProps = {
  documentQuery: ReturnType<typeof useGetDocument>
  commentsOverlay: OverlayState
  commentsCount?: number
  commentsCountIsPending?: boolean
  hasPreviousDocument: boolean
  hasNextDocument: boolean
  selectedIndex: number
  goToPreviousDocument: () => void
  goToNextAvailableDocumentOrClose: () => void
  onClose: () => void
  documentsCount?: number
  documentView?: WorkflowState['default_document_view']
}

export default function ValidationOverlayHeader({
  documentView,
  documentQuery,
  commentsOverlay,
  commentsCount,
  commentsCountIsPending,
  hasPreviousDocument,
  hasNextDocument,
  selectedIndex,
  goToPreviousDocument,
  goToNextAvailableDocumentOrClose,
  onClose,
  documentsCount,
}: ValidationOverlayHeaderProps) {
  const { project } = useProjectContext()
  const actionsMenuOverlay = useOverlay()
  const keyboardShortcutsOverlay = useOverlay()

  const [searchParams] = useSearchParams()
  const documentId = searchParams.get('document_id') || ''

  const { document, refetch, queryKey } = documentQuery

  const { updateDocumentAsync } = useUpdateDocument({
    detailQueryKey: queryKey,
    onError: () => {
      showErrorSnackbar('Failed to update document.')
    },
  })

  const { selectedWorkflow } = useSelectedWorkflowContext()
  const documentsChangeSetsContextData = useDocumentsChangeSetsContext()

  const workflowStates = selectedWorkflow?.workflow_states || []

  const workflowState = workflowStates.find((state) =>
    document?.workflow_states_ids?.some((id) => id === state.id),
  )

  const { updateDocumentsOrg } = useUpdateDocsOrgWithDocChange({
    detailQueryKey: queryKey,
    documentsChangeSetsContextData,
    onError: () => {
      showErrorSnackbar('Failed to update document organization.')
    },
  })

  const queryClient = useQueryClient()
  const { setWorkflowState } = useSetDocumentsWorkflowState({
    onMutate: async ({ patch }) => {
      const { workflow_state_id } = patch
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<Document>(queryKey)
      const newDocument = {
        ...previous,
        workflow_states_ids: [
          workflow_state_id,
          ...(previous?.workflow_states_ids?.filter(
            (workflowStateId) => workflowStateId !== workflowState?.id,
          ) || []),
        ],
      } as Document
      queryClient.setQueryData<Document>(queryKey, newDocument)
    },
    onError: () => {
      showErrorSnackbar('Failed to update document workflow state.')
    },
  })

  const { refetch: refetchRowValues } = useDocumentRowValuesContext()
  const { organizations, isFetchingAll } = useOrganizationsContext()
  const { selectedFieldOverlay } = useSelectedFieldContext()

  function handleUpdateDocumentWorkflowState(newWorkflowState: WorkflowState) {
    if (
      !document ||
      !workflowState ||
      !selectedWorkflow ||
      newWorkflowState.code === workflowState.code
    )
      return
    setWorkflowState({
      patch: {
        workflow_state_id: newWorkflowState.id,
        workflow_state__code: newWorkflowState.code,
      },
      filter_criteria: {
        document_id__in: [document.id],
        workflow_state__workflow_id: selectedWorkflow.id,
      },
      current_workflow_state_id: workflowState.id,
    })
  }

  function handleUpdateOrganization(newOrganization: Organization) {
    if (!document || newOrganization.id === document.owner_org_id) return
    updateDocumentsOrg(
      [
        {
          doc_id: document.id,
          workflow_id: selectedWorkflow.id,
          workflow_state_id: workflowState?.id as string,
        },
      ],
      newOrganization.id,
    )
  }

  const isInProcessingState = workflowState?.code === 'processing'

  const organization = organizations.find(
    ({ id }) => id === document?.owner_org_id,
  ) as Organization

  const keyboardShortcuts = useMemo(
    () => getShortcuts(documentView),
    [documentView],
  )

  // Refetch the document if there are document state changes that include the current document
  useNotifications({
    keys: [documentId],
    callback: ({ action }) => {
      if (documentId) {
        refetch()
        if (
          action === 'document_workflow_state_status_changed' ||
          action === 'document_workflow_state_changed'
        ) {
          refetchRowValues()
        }
      }
    },
  })

  const projectTagsAddedToDoc = useMemo(
    () =>
      project.tags?.filter(({ id }) =>
        document?.project_tags_ids?.includes(id),
      ) || [],
    [document?.project_tags_ids, project.tags],
  )

  return (
    <Box sx={{ p: 1, px: 2 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent={document ? 'space-between' : 'flex-end'}
        sx={{ minHeight: 40 }} // minHeight so it doesn't change height when switching to editing for name
      >
        {document && (
          <EditableDocumentName
            document={document}
            disabled={isInProcessingState}
            updateDocumentAsync={updateDocumentAsync}
          />
        )}

        {/* MARK: Drawer Toggles / Keyboard Shortcuts Button / More Menu */}
        <Stack direction="row" spacing={0.25} alignItems="center">
          {documentView === 'validation' && (
            <DrawerToggleButtons
              commentsCount={commentsCount}
              commentsCountIsPending={commentsCountIsPending}
              commentsOverlay={commentsOverlay}
              fieldDetailsOverlay={selectedFieldOverlay}
              disabled={isInProcessingState}
            />
          )}

          <Tooltip title="Shortcuts" arrow disableInteractive enterDelay={1000}>
            <IconButton
              onClick={() => {
                keyboardShortcutsOverlay.open()
              }}
            >
              <KeyboardAltOutlined />
            </IconButton>
          </Tooltip>

          <IconButton
            size="small"
            onClick={actionsMenuOverlay.open}
            disabled={!document || isInProcessingState}
          >
            <MoreVert />
          </IconButton>

          <IconButton size="small" onClick={onClose}>
            <Close />
          </IconButton>

          {document && (
            <DocumentActionsMenu
              document={document}
              projectId={project.id}
              overlay={actionsMenuOverlay}
              onDelete={onClose}
              displayedOptions={[
                'open-document-dialog',
                'view-workflow-state-documentation',
                're-run-rules',
                'clear-all-chips',
                'clear-events',
                'view-export-jobs',
                'cancel-events',
              ]}
            />
          )}
        </Stack>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          gap: 1,
          alignItems: 'center',
        }}
      >
        {/* MARK: Workflow State, Organization Menus */}
        <Stack
          direction="row"
          flexWrap="wrap"
          sx={{ minWidth: 0, transform: 'translateX(-8px)' }}
        >
          {workflowState && !isInProcessingState && (
            <Box>
              <WorkflowStateSelectMenu
                dense
                selectedWorkflowState={workflowState}
                workflowStates={workflowStates}
                onChange={handleUpdateDocumentWorkflowState}
                selectedWorkflowStateEnteredAt={
                  document?.document_workflow_states?.find(
                    ({ workflow_state_id }) =>
                      workflow_state_id === workflowState.id,
                  )?.entered_at
                }
              />
            </Box>
          )}

          {organization && (
            <Box>
              <OrganizationAssignmentSelect
                dense
                isFetching={isFetchingAll}
                selectedOrganization={organization}
                organizations={organizations}
                onChange={handleUpdateOrganization}
                disabled={isInProcessingState}
                rootTreeOrgId={project.org_id}
                showParentOrg
              />
            </Box>
          )}
        </Stack>

        <DocumentNavigationButtons
          hasPreviousDocument={hasPreviousDocument}
          hasNextDocument={hasNextDocument}
          selectedIndex={selectedIndex}
          goToPreviousDocument={goToPreviousDocument}
          goToNextAvailableDocumentOrClose={goToNextAvailableDocumentOrClose}
          documentsCount={documentsCount}
        />

        {/* MARK: Event Button, Flags Button, Visibility Button, Document Tags */}
        <Stack
          direction="row-reverse"
          flexWrap="wrap"
          columnGap={1}
          rowGap={1}
          sx={{ minWidth: 0 }}
        >
          <Box>
            {workflowState && document && (
              <ValidationEventButton
                document={document}
                goToNextAvailableDocumentOrClose={
                  goToNextAvailableDocumentOrClose
                }
                workflowStateId={workflowState.id}
              />
            )}
          </Box>
          {!isInProcessingState && documentView && (
            <>
              <DocumentViewControl />
              {(documentView === 'validation' ||
                documentView === 'split-document') && (
                <ValidationFlagsButton
                  disableRowValueFlagClick={documentView === 'split-document'}
                />
              )}

              {(documentView === 'validation' ||
                documentView === 'comparison') && (
                <VisibleFieldsControl documentView={documentView} />
              )}

              {documentView === 'validation' && <VisibleCardsControl />}
              <Box sx={{ m: 'auto 0 !important', minWidth: 0 }}>
                <DocProjectTagsList
                  projectTags={projectTagsAddedToDoc}
                  collapse
                />
              </Box>
            </>
          )}
        </Stack>
      </Box>

      <KeyboardShortcutsDialog
        overlay={keyboardShortcutsOverlay}
        keyboardShortcutGroups={keyboardShortcuts}
      />
    </Box>
  )
}
